/**
 * EndNote XML → reference records.
 *
 * EndNote's "Export… → XML" file holds `<xml><records><record>…`, one record per
 * reference, with the reference type as `<ref-type name="Journal Article">17</ref-type>`,
 * names in `<contributors>`, titles in `<titles>`, the journal in `<periodical>`, the date
 * in `<dates>` and the DOI in `<electronic-resource-num>`. Text is often wrapped in
 * `<style>` elements, which are read through. Zotero's "EndNote XML" export uses the same
 * layout.
 *
 * The type name is the reliable key; the number is only a fallback for the types whose
 * numbers EndNote's documentation fixes (17 Journal Article, 6 Book, 5 Book Section,
 * 47 Conference Paper, 10 Conference Proceedings, 32 Thesis, 27 Report, 13 Generic,
 * 12 Web Page, 16 Magazine Article, 23 Newspaper Article).
 */

import {
  type ConvertResult,
  type ConvertWarning,
  classifyStandardNumber,
  composeDate,
  extractDoi,
} from "@/lib/tool-kit/research-discovery/convert";
import {
  emptyRecord,
  type RefName,
  type RefRecord,
  splitPages,
  squash,
} from "@/lib/tool-kit/research-discovery/records";
import {
  childrenNamed,
  descend,
  findAll,
  parseXml,
  textOf,
  type XmlElement,
} from "@/lib/tool-kit/research-discovery/xml";

const TYPE_BY_NAME: Record<string, string> = {
  "journal article": "JOUR",
  "electronic article": "EJOUR",
  "magazine article": "MGZN",
  "newspaper article": "NEWS",
  book: "BOOK",
  "edited book": "EDBOOK",
  "electronic book": "EBOOK",
  "book section": "CHAP",
  "electronic book section": "ECHAP",
  "conference paper": "CPAPER",
  "conference proceedings": "CONF",
  thesis: "THES",
  report: "RPRT",
  generic: "GEN",
  "web page": "ELEC",
  blog: "BLOG",
  manuscript: "MANSCPT",
  "unpublished work": "UNPB",
  dataset: "DATA",
  "online database": "DBASE",
  "aggregated database": "AGGR",
  "computer program": "COMP",
  patent: "PAT",
  statute: "STAT",
  case: "CASE",
  bill: "BILL",
  hearing: "HEAR",
  "government document": "GOVDOC",
  map: "MAP",
  artwork: "ART",
  "audiovisual material": "ADVS",
  "film or broadcast": "VIDEO",
  "personal communication": "PCOMM",
  "press release": "PRESS",
  catalog: "CTLG",
  "chart or table": "CHART",
  "classical work": "CLSWK",
  equation: "EQUA",
  figure: "FIGURE",
  music: "MUSIC",
  serial: "SER",
  standard: "STAND",
  pamphlet: "PAMP",
  "ancient text": "ANCIENT",
};

const TYPE_BY_NUMBER: Record<string, string> = {
  "17": "JOUR",
  "6": "BOOK",
  "5": "CHAP",
  "47": "CPAPER",
  "10": "CONF",
  "32": "THES",
  "27": "RPRT",
  "13": "GEN",
  "12": "ELEC",
  "16": "MGZN",
  "23": "NEWS",
};

/** Types whose container is a periodical, so its name is the journal, magazine or paper. */
const PERIODICAL_TYPES = new Set(["JOUR", "EJOUR", "MGZN", "NEWS"]);
/** Types where a missing author is worth reporting. */
const AUTHORED_TYPES = new Set([
  "JOUR",
  "EJOUR",
  "BOOK",
  "CHAP",
  "CPAPER",
  "THES",
  "RPRT",
]);

const RE_TRAILING_COMMA = /,\s*$/;

/** "Smith, John" → family/given; "World Health Organization," or "Plato" → one literal. */
export function endnoteName(raw: string): RefName | null {
  const value = squash(raw);
  if (value === "") {
    return null;
  }
  const comma = value.indexOf(",");
  if (comma < 0 || RE_TRAILING_COMMA.test(value)) {
    const literal = value.replace(RE_TRAILING_COMMA, "").trim();
    return { family: literal, given: "", literal };
  }
  return {
    family: value.slice(0, comma).trim(),
    given: value.slice(comma + 1).trim(),
  };
}

function names(record: XmlElement, group: string): RefName[] {
  const people = descend(record, ["contributors", group]);
  if (!people) {
    return [];
  }
  return childrenNamed(people, "author").flatMap((author) => {
    const name = endnoteName(textOf(author));
    return name ? [name] : [];
  });
}

function field(record: XmlElement, path: readonly string[]): string {
  return textOf(descend(record, path));
}

function typeOf(record: XmlElement): {
  code: string;
  known: boolean;
  name: string;
} {
  const node = descend(record, ["ref-type"]);
  const name = node?.attrs.name ?? "";
  const number = textOf(node);
  const code = TYPE_BY_NAME[name.toLowerCase()] ?? TYPE_BY_NUMBER[number];
  return code
    ? { code, known: true, name }
    : { code: "GEN", known: false, name: name || number };
}

function urls(record: XmlElement): string[] {
  const related = descend(record, ["urls", "related-urls"]);
  return related
    ? childrenNamed(related, "url")
        .map(textOf)
        .filter((url) => url !== "")
    : [];
}

function convertRecord(
  node: XmlElement,
  index: number,
  warnings: ConvertWarning[]
): RefRecord {
  const type = typeOf(node);
  const record = emptyRecord(type.code);
  if (!type.known) {
    warnings.push({ code: "unknownType", record: index, detail: type.name });
  }
  record.authors = names(node, "authors");
  record.editors = names(node, "secondary-authors");
  record.title = field(node, ["titles", "title"]);
  const periodical = field(node, ["periodical", "full-title"]);
  const secondary = field(node, ["titles", "secondary-title"]);
  record.container = PERIODICAL_TYPES.has(type.code)
    ? periodical || secondary
    : secondary || periodical;
  record.containerAbbrev =
    field(node, ["periodical", "abbr-1"]) ||
    field(node, ["titles", "alt-title"]);
  record.series = field(node, ["titles", "tertiary-title"]);
  record.edition = field(node, ["edition"]);
  record.genre = field(node, ["work-type"]);
  [record.startPage, record.endPage] = splitPages(field(node, ["pages"]));
  record.volume = field(node, ["volume"]);
  record.issue = field(node, ["number"]);
  record.date = composeDate(
    field(node, ["dates", "year"]),
    field(node, ["dates", "pub-dates", "date"])
  );
  record.year = record.date.slice(0, 4);
  record.publisher = field(node, ["publisher"]);
  record.place = field(node, ["pub-location"]);
  Object.assign(record, classifyStandardNumber(field(node, ["isbn"])));
  record.abstract = field(node, ["abstract"]);
  record.language = field(node, ["language"]);
  record.accession = field(node, ["accession-num"]);
  record.database = field(node, ["remote-database-name"]);
  const keywords = descend(node, ["keywords"]);
  record.keywords = keywords
    ? childrenNamed(keywords, "keyword")
        .map(textOf)
        .filter((k) => k !== "")
    : [];
  return finish(node, record, index, warnings);
}

function finish(
  node: XmlElement,
  record: RefRecord,
  index: number,
  warnings: ConvertWarning[]
): RefRecord {
  const doiField = field(node, ["electronic-resource-num"]);
  record.doi = extractDoi(doiField);
  if (doiField !== "" && record.doi === "") {
    record.notes.push(doiField);
    warnings.push({ code: "doiUnreadable", record: index, detail: doiField });
  }
  const [first, ...more] = urls(node);
  record.url = first ?? "";
  record.notes.push(...more);
  for (const note of [
    field(node, ["notes"]),
    field(node, ["research-notes"]),
  ]) {
    if (note !== "") {
      record.notes.push(note);
    }
  }
  if (record.title === "") {
    warnings.push({ code: "noTitle", record: index });
  }
  if (record.authors.length === 0 && AUTHORED_TYPES.has(record.type)) {
    warnings.push({ code: "noAuthors", record: index });
  }
  return record;
}

/** Convert an EndNote XML export. */
export function convertEndnoteXml(source: string): ConvertResult {
  if (source.trim() === "") {
    return { records: [], warnings: [], sourceCount: 0, problem: "empty" };
  }
  const { root, errors } = parseXml(source);
  const nodes = findAll(root, "record");
  const warnings: ConvertWarning[] = errors.map((detail) => ({
    code: "xmlProblem",
    record: 0,
    detail,
  }));
  if (nodes.length === 0) {
    return { records: [], warnings, sourceCount: 0, problem: "noRecords" };
  }
  const records = nodes.map((node, index) =>
    convertRecord(node, index + 1, warnings)
  );
  return { records, warnings, sourceCount: nodes.length };
}
